import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import jQuery from 'jquery';
import App from './App';

// Register GSAP plugins
gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

const PDP_C7 = {
  init: function () {
    console.log('PDP_C7: initializing');

    // Add breadcrumbs to the page
    this.addBreadcrumbs();

    // Extract and set product subtitle
    this.extractProductSubtitle();

    // Initialize cleanup for product description
    this.cleanupProductDescription();

    // Initialize image gallery functionality
    this.initImageGallery();

    // Initialize quantity controls
    this.initQuantityControls();

    // Update add to cart button
    this.updateAddToCartButton();

    // Handle product recommendations
    this.initProductRecommendations();

    // Refresh ScrollTrigger after content is loaded
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 1000);
  },

  addBreadcrumbs: function () {
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      // Check if breadcrumbs already exist
      if (document.getElementById('breadcrumbs')) {
        obs.disconnect();
        return;
      }

      const c7Content = document.getElementById('c7-content');
      if (!c7Content) return;

      const productTitle = document.querySelector('.product-title');
      if (!productTitle) return;

      // Create breadcrumbs container
      const breadcrumbsContainer = document.createElement('ul');
      breadcrumbsContainer.id = 'breadcrumbs';

      // Add breadcrumb items
      breadcrumbsContainer.innerHTML = `
        <li><a href="/">Home</a></li>
        <li><a href="/wines">Wines</a></li>
        <li><a href="/shop">Shop</a></li>
      `;

      // Create a wrapper for the breadcrumbs
      const breadcrumbsWrapper = document.createElement('div');
      breadcrumbsWrapper.className = 'breadcrumbs-wrapper';
      breadcrumbsWrapper.appendChild(breadcrumbsContainer);

      // Insert the breadcrumbs at the top of the c7-content element
      const productDetailSection = document.querySelector('.c7-product-detail');
      if (productDetailSection) {
        productDetailSection.insertBefore(breadcrumbsWrapper, productDetailSection.firstChild);

        // Disconnect observer once breadcrumbs are added
        obs.disconnect();
      }
    });

    // Start observing the document for content changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  },

  initProductRecommendations: function () {
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      const personalizationSection = document.querySelector('.c7-personalization');
      if (!personalizationSection) return;

      const personalizationContent = personalizationSection.querySelector(
        '.c7-personalization__content'
      );
      if (!personalizationContent) return;

      // Check if our customization has already been applied
      if (personalizationSection.classList.contains('recommendations-styled')) {
        return;
      }

      // Hide intro text completely if the suggestions copy div exists
      const suggestionsCopy = personalizationContent.querySelector('.suggestions-copy');
      if (suggestionsCopy) {
        suggestionsCopy.style.display = 'none';
      }

      // Apply our custom styling directly with JS for better specificity
      const productList = personalizationSection.querySelector(
        '.c7-product-collection__product-list'
      );
      if (productList) {
        productList.style.display = 'grid';
        productList.style.gridTemplateColumns = 'repeat(3, 1fr)';
        productList.style.gap = '2rem';
      }

      // Add quantity controls to each product in the recommendations
      const quantityInputs = personalizationSection.querySelectorAll(
        '.c7-product__add-to-cart__form__quantity input'
      );
      if (quantityInputs.length > 0) {
        quantityInputs.forEach((input) => {
          const container = input.closest('.c7-product__add-to-cart__form__quantity');
          if (container && !container.querySelector('.quantity-controls')) {
            this.addQuantityControls(input, container);
          }
        });
      }

      // Make sure the price variant is hidden
      const priceVariants = personalizationSection.querySelectorAll(
        '.c7-product__add-to-cart__price__variant'
      );
      priceVariants.forEach((variant) => {
        variant.style.display = 'none';
      });

      // Add hover effect to the products
      const products = personalizationSection.querySelectorAll('.c7-product');
      products.forEach((product) => {
        product.addEventListener('mouseenter', () => {
          product.style.transform = 'translateY(-5px)';
        });

        product.addEventListener('mouseleave', () => {
          product.style.transform = 'translateY(0)';
        });
      });

      // Mark as styled to prevent multiple applications
      personalizationSection.classList.add('recommendations-styled');

      // Check if there are any products
      if (products.length > 0) {
        // Disconnect once we've processed the recommendations
        obs.disconnect();
      }
    });

    // Start observing the document for content changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  },

  addQuantityControls: function (input, container) {
    // Create controls container
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'quantity-controls';

    // Create increment button
    const incrementBtn = document.createElement('button');
    incrementBtn.className = 'quantity-increase';
    incrementBtn.setAttribute('type', 'button');
    incrementBtn.textContent = '+';
    incrementBtn.addEventListener('click', () => {
      const currentValue = parseInt(input.value, 10) || 1;
      input.value = currentValue + 1;
      // Trigger change event to update any listeners
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Create decrement button
    const decrementBtn = document.createElement('button');
    decrementBtn.className = 'quantity-decrease';
    decrementBtn.setAttribute('type', 'button');
    decrementBtn.textContent = '-';
    decrementBtn.addEventListener('click', () => {
      const currentValue = parseInt(input.value, 10) || 1;
      if (currentValue > 1) {
        input.value = currentValue - 1;
        // Trigger change event to update any listeners
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Add buttons to controls container
    controlsContainer.appendChild(decrementBtn);
    controlsContainer.appendChild(incrementBtn);

    // Add controls container to the quantity input's parent
    container.appendChild(controlsContainer);
  },

  extractProductSubtitle: function () {
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      const productTitle = document.querySelector('.product-title');
      const subtitleElement = document.querySelector('.product-subtitle');

      if (!productTitle || !subtitleElement) return;

      const titleText = productTitle.textContent.trim();

      // Extract subtitle patterns based on the requirements
      let subtitle = '';

      // Pattern 1: "{vintage} Napa Valley" or "{vintage} napa valley"
      let napaValleyMatch = titleText.match(/^(\d{4})\s+Napa\s+Valley/i);
      if (napaValleyMatch) {
        // Extract "2019 Napa Valley" from the title
        subtitle = napaValleyMatch[0];

        // Update the product title to remove the extracted part
        const remainingTitle = titleText.replace(subtitle, '').trim();
        if (remainingTitle) {
          productTitle.textContent = remainingTitle;
        }
      }
      // Pattern 2: "{vintage} Family Reserve"
      else if (titleText.match(/^(\d{4})\s+Family\s+Reserve/i)) {
        const familyReserveMatch = titleText.match(/^(\d{4})\s+Family\s+Reserve/i);
        subtitle = familyReserveMatch[0];

        // Update the product title to remove the extracted part
        const remainingTitle = titleText.replace(subtitle, '').trim();
        if (remainingTitle) {
          productTitle.textContent = remainingTitle;
        }
      }
      // Pattern 3: "{vintage} Limited Release"
      else if (titleText.match(/^(\d{4})\s+Limited\s+Release/i)) {
        const limitedReleaseMatch = titleText.match(/^(\d{4})\s+Limited\s+Release/i);
        subtitle = limitedReleaseMatch[0];

        // Update the product title to remove the extracted part
        const remainingTitle = titleText.replace(subtitle, '').trim();
        if (remainingTitle) {
          productTitle.textContent = remainingTitle;
        }
      }
      // Pattern 4: "{vintage} Vintage Selection"
      else if (titleText.match(/^(\d{4})\s+Vintage\s+Selection/i)) {
        const vintageSelectionMatch = titleText.match(/^(\d{4})\s+Vintage\s+Selection/i);
        subtitle = vintageSelectionMatch[0];

        // Update the product title to remove the extracted part
        const remainingTitle = titleText.replace(subtitle, '').trim();
        if (remainingTitle) {
          productTitle.textContent = remainingTitle;
        }
      }

      // Set the subtitle if we found a match
      if (subtitle) {
        subtitleElement.innerHTML = `<span>${subtitle}</span>`;

        // Disconnect once we've set the subtitle
        obs.disconnect();
      }
    });

    // Start observing the c7-content element
    const c7Content = document.getElementById('c7-content');
    if (c7Content) {
      observer.observe(c7Content, {
        childList: true,
        subtree: true,
      });
    }
  },

  cleanupProductDescription: function () {
    console.log('PDP_C7: Starting product description cleanup');
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      const productDescription = document.querySelector('.product-description');
      if (!productDescription) {
        console.log('PDP_C7: No product description found');
        return;
      }

      // Make sure we're only running once the content is fully loaded
      if (productDescription.dataset.cleaned === 'true') {
        console.log('PDP_C7: Product description already cleaned');
        return;
      }

      console.log('PDP_C7: Found product description, cleaning up...');

      // Clean up unnecessary <br> tags and empty divs
      this.cleanupHtmlContent(productDescription);

      // Mark as cleaned to prevent multiple processing
      productDescription.dataset.cleaned = 'true';
      console.log('PDP_C7: Product description cleanup completed');

      // Disconnect once we've cleaned up
      obs.disconnect();
    });

    // Start observing the c7-content element
    const c7Content = document.getElementById('c7-content');
    if (c7Content) {
      observer.observe(c7Content, {
        childList: true,
        subtree: true,
      });
      console.log('PDP_C7: Observing c7-content for product description changes');
    } else {
      console.log('PDP_C7: c7-content element not found');
    }
  },

  cleanupHtmlContent: function (container) {
    if (!container) {
      console.log('PDP_C7: cleanupHtmlContent - No container provided');
      return;
    }

    console.log('PDP_C7: cleanupHtmlContent - Starting cleanup');

    // Remove unnecessary <br> tags
    const brTags = container.querySelectorAll('br');
    console.log(`PDP_C7: Found ${brTags.length} <br> tags`);
    brTags.forEach((br) => {
      // Check if it's at the end of a styled span
      const parent = br.parentNode;
      if (
        parent &&
        parent.tagName === 'SPAN' &&
        (parent.getAttribute('style')?.includes('font-weight') ||
          parent.getAttribute('style')?.includes('font-style'))
      ) {
        br.remove();
      }
    });

    // Remove empty divs
    const divs = container.querySelectorAll('div');
    console.log(`PDP_C7: Found ${divs.length} div elements`);
    let emptyDivsRemoved = 0;
    divs.forEach((div) => {
      if (div.innerHTML.trim() === '' || div.innerHTML === '<br>') {
        div.remove();
        emptyDivsRemoved++;
      }
    });
    console.log(`PDP_C7: Removed ${emptyDivsRemoved} empty divs`);

    // Convert list-like items into proper lists
    console.log('PDP_C7: Starting list conversion');
    this.convertListItems(container);

    // Combine adjacent spans with same styling
    console.log('PDP_C7: Starting span combination');
    this.combineAdjacentSpans(container);

    // Structure the description content better
    console.log('PDP_C7: Starting content restructuring');
    this.restructureDescriptionContent(container);

    console.log('PDP_C7: HTML cleanup completed');
  },

  convertListItems: function (container) {
    // Get all the divs in the container
    const divs = Array.from(container.querySelectorAll(':scope > div'));
    console.log(`PDP_C7: convertListItems - Found ${divs.length} direct child divs`);

    if (divs.length === 0) {
      console.log('PDP_C7: convertListItems - No divs to process');
      return;
    }

    // Special handling for when all content is in a single div
    if (divs.length === 1) {
      console.log('PDP_C7: Single div detected, checking for list content within it');
      const singleDiv = divs[0];
      const fullText = singleDiv.textContent.trim();

      // Look for 'Contains one bottle each of:' pattern in the text
      if (fullText.includes('Contains one bottle each of:')) {
        console.log('PDP_C7: Found "Contains one bottle each of:" pattern in single div');

        // Extract list items by splitting the text
        const parts = fullText.split('Contains one bottle each of:');
        if (parts.length > 1) {
          // Get all text after "Contains one bottle each of:"
          let afterMarker = parts[1];

          // Check if we have a price at the end
          const priceMatch = afterMarker.match(/(Special|Set|Package|Total) Price: \$\d+/);
          let priceText = '';

          if (priceMatch) {
            console.log(`PDP_C7: Found price: "${priceMatch[0]}"`);
            // Extract the price text
            priceText = priceMatch[0];
            // Remove price from the list items text
            afterMarker = afterMarker.replace(priceMatch[0], '');
          }

          // Extract list items better
          // Common wine products that can be identified as separate items
          const winePatterns = [
            /\d{4}\s+Carneros\s+Chardonnay/i,
            /\d{4}\s+Carneros\s+Pinot\s+Noir/i,
            /\d{4}\s+Estate\s+Virgin\s+Olive\s+Oil/i,
            /Lot\s+X{1,3}I{0,3}\s+Zinfandel\s+Dessert\s+Wine/i,
            /\d{4}\s+Cabernet\s+Sauvignon/i,
            /\d{4}\s+Merlot/i,
            /\d{4}\s+Vintage\s+Selection/i,
            /\d{4}\s+Family\s+Reserve/i,
            /\d{4}\s+Limited\s+Release/i,
            /\d{4}\s+Napa\s+Valley/i,
          ];

          // First, try to identify known wine product patterns
          const potentialItems = [];
          let remainingText = afterMarker;

          console.log('PDP_C7: Searching for wine patterns in: ' + remainingText);

          // Iterate through wine patterns to find matches
          winePatterns.forEach((pattern) => {
            const matches = remainingText.match(new RegExp(pattern, 'g'));
            if (matches) {
              matches.forEach((match) => {
                console.log(`PDP_C7: Found wine pattern match: "${match}"`);
                potentialItems.push(match);
                // Remove the matched item from the remaining text to avoid duplicates
                remainingText = remainingText.replace(match, '••ITEM_REMOVED••');
              });
            }
          });

          // If we couldn't identify specific patterns, try harder with some heuristics
          if (potentialItems.length === 0) {
            console.log('PDP_C7: No specific wine patterns detected, trying alternate method');

            // Try to split by common separators that might be in the text
            const possibleSeparators = [
              /\d{4}\s+/g, // Year patterns like "2018 "
              /\s*,\s*/g, // Commas with optional spaces
              /\s*\|\s*/g, // Pipes with optional spaces
              /\s*\n\s*/g, // Newlines with optional spaces
              /\s*\.\s*/g, // Periods with optional spaces
              /\s*•\s*/g, // Bullet points with optional spaces
              /\s*-\s*/g, // Hyphens with optional spaces
            ];

            // Try each separator until we find one that gives us a reasonable number of items
            for (const separator of possibleSeparators) {
              const splitItems = afterMarker.split(separator).filter((item) => {
                const trimmed = item.trim();
                return (
                  trimmed && trimmed.length > 3 && !trimmed.match(/Special|Set|Package|Total/i)
                );
              });

              if (splitItems.length >= 2) {
                console.log(
                  `PDP_C7: Successfully split items using separator pattern ${separator}`
                );
                splitItems.forEach((item) => potentialItems.push(item.trim()));
                break;
              }
            }

            // If we still don't have items, try one more desperate approach - look for capitalized words
            if (potentialItems.length === 0) {
              console.log('PDP_C7: Trying capitalized word detection');

              // Split by capital letters preceded by non-capital or space
              const capitalizedSections = afterMarker.match(
                /(\s|^)[A-Z][a-zA-Z0-9\s]+?(?=(\s[A-Z]|$))/g
              );
              if (capitalizedSections && capitalizedSections.length > 1) {
                console.log(`PDP_C7: Found ${capitalizedSections.length} capitalized sections`);
                capitalizedSections.forEach((section) => {
                  const trimmed = section.trim();
                  if (
                    trimmed &&
                    trimmed.length > 3 &&
                    !trimmed.match(/Special|Set|Package|Total/i)
                  ) {
                    potentialItems.push(trimmed);
                  }
                });
              }
            }
          }

          // Final fallback - if nothing else worked, try to split by numbers
          if (potentialItems.length === 0) {
            console.log('PDP_C7: Falling back to number-based splitting');

            // Look for year-like patterns (4 digits) and use them as separators
            const yearMatches = afterMarker.match(/\d{4}/g);
            if (yearMatches && yearMatches.length > 0) {
              let items = [];
              // Split the text around the years
              const yearParts = afterMarker.split(/\d{4}/);

              // Now reconstruct with the years included
              for (let i = 0; i < yearMatches.length; i++) {
                if (i < yearParts.length - 1) {
                  // Skip the first part if it's empty (happens when the text starts with a year)
                  if (i > 0 || yearParts[0].trim()) {
                    const item =
                      yearMatches[i] + yearParts[i + 1].split(/[,.|]|Special|Set|Price/)[0];
                    items.push(item.trim());
                  }
                }
              }

              // Add any valid items we found
              items.forEach((item) => {
                if (item.trim().length > 3) {
                  potentialItems.push(item.trim());
                }
              });
            }
          }

          console.log(`PDP_C7: Found ${potentialItems.length} potential list items`);

          if (potentialItems.length > 0) {
            console.log('PDP_C7: Creating list from single div content');

            // Create intro text div
            const introText = parts[0].trim();
            const introDiv = document.createElement('div');
            introDiv.textContent = introText;

            // Create list marker div
            const listMarkerDiv = document.createElement('div');
            listMarkerDiv.textContent = 'Contains one bottle each of:';

            // Create the list
            const ul = document.createElement('ul');
            ul.className = 'product-list';

            // Add list items
            potentialItems.forEach((item) => {
              const trimmedItem = item.trim();
              if (trimmedItem) {
                const li = document.createElement('li');
                li.textContent = trimmedItem;
                ul.appendChild(li);
                console.log(`PDP_C7: Added list item: "${trimmedItem}"`);
              }
            });

            // Create price div if we have price text
            const priceDiv = document.createElement('div');
            if (priceText) {
              priceDiv.textContent = priceText;
              priceDiv.className = 'product-price-info';
            }

            // Clear the container and add our structured content
            container.innerHTML = '';
            container.appendChild(introDiv);
            container.appendChild(listMarkerDiv);
            container.appendChild(ul);
            if (priceText) {
              container.appendChild(priceDiv);
            }

            console.log('PDP_C7: Restructured single div into formatted content');
            return;
          }
        }
      }
    }

    // Check for a specific pattern like "Contains one bottle each of:" or similar
    let listStartIndex = -1;
    let listItems = [];

    // Find potential list markers
    for (let i = 0; i < divs.length; i++) {
      const text = divs[i].textContent.trim();
      console.log(`PDP_C7: Div ${i} text: "${text}"`);

      // Match more patterns that might indicate a list is starting
      if (
        (text.match(
          /Contains|Includes|Set includes|Package includes|Pack includes|one bottle each of/i
        ) &&
          (text.endsWith(':') || text.includes('of:'))) ||
        text === 'Contains one bottle each of:'
      ) {
        listStartIndex = i;
        console.log(`PDP_C7: Found list marker at index ${i}: "${text}"`);
        break;
      }
    }

    // If no explicit list marker found, look for patterns in the content
    if (listStartIndex === -1) {
      console.log('PDP_C7: No explicit list marker found, looking for patterns');
      // Try to identify list-like structure by looking for consecutive items with <br> between them
      let consecutiveItems = 0;
      let potentialListStart = -1;

      for (let i = 0; i < divs.length - 2; i++) {
        // If a div is followed by a <br> div and then another content div, it might be a list
        if (
          divs[i].textContent.trim() &&
          i + 1 < divs.length &&
          (divs[i + 1].innerHTML === '<br>' || divs[i + 1].textContent.trim() === '') &&
          i + 2 < divs.length &&
          divs[i + 2].textContent.trim()
        ) {
          if (potentialListStart === -1) {
            potentialListStart = i;
          }
          consecutiveItems++;
          console.log(`PDP_C7: Found potential list pattern at index ${i}`);

          // If we've found at least 2 items that look like a list, consider it a list
          if (consecutiveItems >= 2) {
            listStartIndex = potentialListStart;
            console.log(`PDP_C7: Identified list-like pattern starting at index ${listStartIndex}`);
            // Go back one more item if it looks like an introduction
            if (listStartIndex > 0 && divs[listStartIndex - 1].textContent.trim().length > 20) {
              listStartIndex--;
              console.log(`PDP_C7: Adjusted list start to ${listStartIndex} to include intro`);
            }
            break;
          }
        } else {
          // Reset the counter if pattern breaks
          if (consecutiveItems > 0) {
            console.log('PDP_C7: List pattern broken, resetting');
          }
          consecutiveItems = 0;
          potentialListStart = -1;
        }
      }
    }

    // If we found a list marker or pattern and there are divs after it
    if (listStartIndex !== -1) {
      console.log(`PDP_C7: Processing list starting at index ${listStartIndex}`);
      // Start processing from the div after the list marker (or from the list marker itself if it's part of the list)
      let currentIndex = listStartIndex;

      // If the first item is a list header (not an actual list item), start from the next item
      const firstItemText = divs[listStartIndex].textContent.trim();
      if (
        firstItemText.match(/Contains|Includes|Set includes|Package includes|Pack includes/i) &&
        (firstItemText.endsWith(':') || firstItemText.includes('of:'))
      ) {
        currentIndex++;
        console.log(`PDP_C7: First item is a list header, starting from index ${currentIndex}`);
      }

      // Skip any empty divs or <br> divs
      while (
        currentIndex < divs.length &&
        (divs[currentIndex].innerHTML === '<br>' || divs[currentIndex].textContent.trim() === '')
      ) {
        console.log(`PDP_C7: Skipping empty div at index ${currentIndex}`);
        currentIndex++;
      }

      // Collect list items until we hit a div that looks like it's not part of the list
      while (currentIndex < divs.length) {
        const div = divs[currentIndex];
        const text = div.textContent.trim();

        // If empty or just a <br>, skip it
        if (text === '' || div.innerHTML === '<br>') {
          console.log(`PDP_C7: Skipping empty item at index ${currentIndex}`);
          currentIndex++;
          continue;
        }

        // If it looks like a list item (not empty and not a price/metadata information)
        if (
          text &&
          !text.match(/^(Special|Set|Package|Total) Price:/i) &&
          !text.startsWith("Samantha's Picks")
        ) {
          listItems.push(text);
          console.log(`PDP_C7: Added list item: "${text}"`);
        } else if (text.match(/^(Special|Set|Package|Total) Price:/i)) {
          // We've reached pricing info, so stop processing list items
          console.log(`PDP_C7: Found price info, stopping list: "${text}"`);
          break;
        } else {
          console.log(`PDP_C7: Skipping non-list item: "${text}"`);
        }

        currentIndex++;
      }

      console.log(`PDP_C7: Collected ${listItems.length} list items`);

      // If we found list items, create an actual HTML list
      if (listItems.length > 0) {
        console.log('PDP_C7: Creating HTML list element');
        // Create the list
        const ul = document.createElement('ul');
        ul.className = 'product-list';

        // Add each item to the list
        listItems.forEach((item) => {
          const li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
          console.log(`PDP_C7: Added <li> for: "${item}"`);
        });

        // Find the list marker div if it exists
        const listMarkerDiv = listStartIndex >= 0 ? divs[listStartIndex] : null;
        console.log(`PDP_C7: List marker div found: ${Boolean(listMarkerDiv)}`);

        // If the first item is a list header, keep it and insert the list after it
        if (
          listMarkerDiv &&
          listMarkerDiv.textContent
            .trim()
            .match(
              /Contains|Includes|Set includes|Package includes|Pack includes|one bottle each of/i
            )
        ) {
          console.log('PDP_C7: Inserting list after list marker');
          // Remove all the divs that were converted to list items and any <br> divs between them
          let divsToRemove = [];
          for (let i = listStartIndex + 1; i < currentIndex; i++) {
            if (divs[i]) divsToRemove.push(divs[i]);
          }

          console.log(
            `PDP_C7: Removing ${divsToRemove.length} divs that were converted to list items`
          );
          divsToRemove.forEach((div) => div.remove());

          // Insert the list after the list marker
          if (listMarkerDiv.nextSibling) {
            container.insertBefore(ul, listMarkerDiv.nextSibling);
            console.log('PDP_C7: Inserted list after list marker div');
          } else {
            container.appendChild(ul);
            console.log('PDP_C7: Appended list to container (no next sibling)');
          }
        } else {
          console.log('PDP_C7: No list marker, replacing all items with the list');
          // There was no explicit list marker, so replace all the list items and <br>s with our list
          let divsToRemove = [];
          for (let i = listStartIndex; i < currentIndex; i++) {
            if (divs[i]) divsToRemove.push(divs[i]);
          }

          console.log(`PDP_C7: Removing ${divsToRemove.length} divs containing list items`);
          divsToRemove.forEach((div) => div.remove());

          // Insert the list at the beginning of where the list items were
          if (divsToRemove.length > 0 && divsToRemove[0].previousSibling) {
            container.insertBefore(ul, divsToRemove[0].previousSibling.nextSibling);
            console.log('PDP_C7: Inserted list at correct position');
          } else {
            container.appendChild(ul);
            console.log('PDP_C7: Appended list to container (no reference point)');
          }
        }
      } else {
        console.log('PDP_C7: No list items collected, skipping list creation');
      }
    } else {
      console.log('PDP_C7: No list pattern detected in the content');
    }

    // Process price information if it exists
    const priceDivs = Array.from(container.querySelectorAll(':scope > div')).filter((div) =>
      div.textContent.trim().match(/^(Special|Set|Package|Total) Price:/i)
    );

    console.log(`PDP_C7: Found ${priceDivs.length} price divs`);
    if (priceDivs.length > 0) {
      priceDivs.forEach((div) => {
        div.className = 'product-price-info';
        console.log(`PDP_C7: Added class to price div: "${div.textContent.trim()}"`);
      });
    }
  },

  combineAdjacentSpans: function (container) {
    // First pass: identify spans with the same styling
    const spans = container.querySelectorAll('span[style]');
    for (let i = 0; i < spans.length - 1; i++) {
      const currentSpan = spans[i];
      const nextSpan = spans[i + 1];

      // If they're siblings and have the same styling
      if (
        currentSpan.nextSibling === nextSpan &&
        currentSpan.getAttribute('style') === nextSpan.getAttribute('style')
      ) {
        // Combine the content
        currentSpan.innerHTML += nextSpan.innerHTML;
        nextSpan.remove();
        // Rerun the function to catch any new adjacencies
        this.combineAdjacentSpans(container);
        break;
      }
    }
  },

  restructureDescriptionContent: function (container) {
    // Create better structure for the content
    const highlightSection = document.createElement('div');
    highlightSection.className = 'product-highlights';

    const wineSection = document.createElement('div');
    wineSection.className = 'wine-description';

    // Process each div in the description
    const contentDivs = Array.from(container.querySelectorAll(':scope > div'));

    // Find the "THE WINE" heading index
    const wineHeadingIndex = contentDivs.findIndex((div) =>
      div.textContent.trim().includes('THE WINE')
    );

    if (wineHeadingIndex !== -1) {
      // Add content before "THE WINE" to highlights
      contentDivs.slice(0, wineHeadingIndex).forEach((div) => {
        highlightSection.appendChild(div.cloneNode(true));
      });

      // Add "THE WINE" and following content to wine section
      contentDivs.slice(wineHeadingIndex).forEach((div) => {
        wineSection.appendChild(div.cloneNode(true));
      });

      // Clear original container and add new structured content
      container.innerHTML = '';
      container.appendChild(highlightSection);
      container.appendChild(wineSection);
    }
  },

  initImageGallery: function () {
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      const thumbsContainer = document.querySelector('.c7-product-detail__photo__thumbs');
      if (!thumbsContainer) return;

      const thumbLinks = thumbsContainer.querySelectorAll('a');
      const mainImage = document.querySelector('.featured-img-container > img');

      if (thumbLinks.length && mainImage) {
        // Add click event listeners to thumbnails
        thumbLinks.forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const thumbImg = link.querySelector('img');
            if (thumbImg) {
              // Get large image URL
              const largeImageUrl = thumbImg.src.replace('/small/', '/large/');

              // Update main image
              mainImage.src = largeImageUrl;

              // Update active state
              thumbLinks.forEach((l) => l.classList.remove('active'));
              link.classList.add('active');
            }
          });
        });

        // Set first thumbnail as active
        if (thumbLinks[0]) {
          thumbLinks[0].classList.add('active');
        }

        // Disconnect once we've set up the gallery
        obs.disconnect();
      }
    });

    // Start observing the c7-content element
    const c7Content = document.getElementById('c7-content');
    if (c7Content) {
      observer.observe(c7Content, {
        childList: true,
        subtree: true,
      });
    }
  },

  initQuantityControls: function () {
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      const quantityInput = document.querySelector(
        '.c7-product__add-to-cart__form__quantity input'
      );
      if (!quantityInput) return;

      // Create and add quantity controls
      const quantityContainer = quantityInput.closest('.c7-product__add-to-cart__form__quantity');

      if (quantityContainer && !quantityContainer.querySelector('.quantity-controls')) {
        this.addQuantityControls(quantityInput, quantityContainer);

        // Disconnect once we've added the controls
        obs.disconnect();
      }
    });

    // Start observing the c7-content element
    const c7Content = document.getElementById('c7-content');
    if (c7Content) {
      observer.observe(c7Content, {
        childList: true,
        subtree: true,
      });
    }
  },

  updateAddToCartButton: function () {
    // Wait for Commerce7 content to load
    const observer = new MutationObserver((mutations, obs) => {
      const addToCartButton = document.querySelector('.c7-product__add-to-cart__form .c7-btn');
      if (!addToCartButton) return;

      // Check if button already has our custom styling
      if (addToCartButton.classList.contains('custom-styled')) return;

      // Update the button text if needed
      if (addToCartButton.querySelector('span')) {
        addToCartButton.querySelector('span').textContent = 'Add To Cart';
      }

      // Add our custom class
      addToCartButton.classList.add('custom-styled');

      // Disconnect once we've updated the button
      obs.disconnect();
    });

    // Start observing the c7-content element
    const c7Content = document.getElementById('c7-content');
    if (c7Content) {
      observer.observe(c7Content, {
        childList: true,
        subtree: true,
      });
    }
  },
};

export default PDP_C7;
